//@flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import { AuditMetadata } from 'common/models/flow/AuditMetadata';
import { TeamId } from 'common/models/flow/TeamId';
import { RoleId } from './RoleId';
import { TransporterId } from './TransporterId';

import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { EmailAddressModelType } from 'common/models/flow/EmailAddress';
import type { PhoneNumberModelType } from 'common/models/flow/PhoneNumber';
import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { BusinessUnitIdModelType } from './BusinessUnits';
import type { RoleIdModelType } from './RoleId';
import type { RoleModelType } from './Role';
import type { TransporterIdModelType } from './TransporterId';
import type { PersonnelTypeEnumType } from 'common/enums/PersonnelEnums';

type PersonnelStatusEnum = 'ACTIVE' | 'INACTIVE';
type AccessTypeEnum = 'READ' | 'WRITE';

export type MasterPersonnelModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    email: EmailAddressModelType,
    phone: ?PhoneNumberModelType,
    customRole: ?string,
    hasAllTeamsAccess: boolean,
    teams: Array<TeamIdModelType>,
    canManageEntities: boolean,
    canVerifyEntities: boolean,
    canManagePersonnel: boolean,
    roleMasterId: RoleIdModelType,
    roleMaster: ?RoleModelType,
    accessType: AccessTypeEnum,
    status: PersonnelStatusEnum,
    auditMetadata: ?AuditMetadataModelType,
    version: number,
    customProperties: { [key: string]: string },
    photoUrl: ?string,
    language: string,
    timezone: string,
    businessUnitIds: Array<BusinessUnitIdModelType>,
    personnelType: PersonnelTypeEnumType,
    transporters: Array<TransporterIdModelType>
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterPersonnel extends ImmutableModel {
    static fromJS(json: MasterPersonnelModelType): MasterPersonnel {
        const state: Object = Object.assign({}, json);
        state.email = EmailAddress.fromJS(state.email);
        state.phone = state.phone
            ? PhoneNumber.fromJS(state.phone)
            : state.phone;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.roleMasterId = RoleId.fromJS(state.roleMasterId);
        state.roleMaster = state.roleMaster
            ? Role.fromJS(state.roleMaster)
            : state.roleMaster;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        state.businessUnitIds = Immutable.List(
            state.businessUnitIds
        ).map(item => BusinessUnitId.fromJS(item));
        state.transporters = Immutable.List(state.transporters).map(item =>
            TransporterId.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): MasterPersonnelModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            email: this.email.toJS(),
            phone: this.phone ? this.phone.toJS() : this.phone,
            customRole: this.customRole,
            hasAllTeamsAccess: this.hasAllTeamsAccess,
            teams: this.teams.toArray().map(item => item.toJS()),
            canManageEntities: this.canManageEntities,
            canVerifyEntities: this.canVerifyEntities,
            canManagePersonnel: this.canManagePersonnel,
            roleMasterId: this.roleMasterId.toJS(),
            roleMaster: this.roleMaster
                ? this.roleMaster.toJS()
                : this.roleMaster,
            accessType: this.accessType,
            status: this.status,
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            version: this.version,
            customProperties: this.customProperties.toObject(),
            photoUrl: this.photoUrl,
            language: this.language,
            timezone: this.timezone,
            businessUnitIds: this.businessUnitIds
                .toArray()
                .map(item => item.toJS()),
            personnelType: this.personnelType,
            transporters: this.transporters.toArray().map(item => item.toJS())
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get email(): EmailAddress {
        return this._state.get('email');
    }

    setEmail(email: EmailAddress): this {
        return this.clone(this._state.set('email', email));
    }

    get phone(): ?PhoneNumber {
        return this._state.get('phone');
    }

    setPhone(phone: ?PhoneNumber): this {
        return this.clone(this._state.set('phone', phone));
    }

    get customRole(): ?string {
        return this._state.get('customRole');
    }

    setCustomRole(customRole: ?string): this {
        return this.clone(this._state.set('customRole', customRole));
    }

    get hasAllTeamsAccess(): boolean {
        return this._state.get('hasAllTeamsAccess');
    }

    setHasAllTeamsAccess(hasAllTeamsAccess: boolean): this {
        return this.clone(
            this._state.set('hasAllTeamsAccess', hasAllTeamsAccess)
        );
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get canManageEntities(): boolean {
        return this._state.get('canManageEntities');
    }

    setCanManageEntities(canManageEntities: boolean): this {
        return this.clone(
            this._state.set('canManageEntities', canManageEntities)
        );
    }

    get canVerifyEntities(): boolean {
        return this._state.get('canVerifyEntities');
    }

    setCanVerifyEntities(canVerifyEntities: boolean): this {
        return this.clone(
            this._state.set('canVerifyEntities', canVerifyEntities)
        );
    }

    get canManagePersonnel(): boolean {
        return this._state.get('canManagePersonnel');
    }

    setCanManagePersonnel(canManagePersonnel: boolean): this {
        return this.clone(
            this._state.set('canManagePersonnel', canManagePersonnel)
        );
    }

    get roleMasterId(): RoleId {
        return this._state.get('roleMasterId');
    }

    setRoleMasterId(roleMasterId: RoleId): this {
        return this.clone(this._state.set('roleMasterId', roleMasterId));
    }

    get roleMaster(): ?Role {
        return this._state.get('roleMaster');
    }

    setRoleMaster(roleMaster: ?Role): this {
        return this.clone(this._state.set('roleMaster', roleMaster));
    }

    get accessType(): AccessTypeEnum {
        return this._state.get('accessType');
    }

    setAccessType(accessType: AccessTypeEnum): this {
        return this.clone(this._state.set('accessType', accessType));
    }

    get status(): PersonnelStatusEnum {
        return this._state.get('status');
    }

    setStatus(status: PersonnelStatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get photoUrl(): ?string {
        return this._state.get('photoUrl');
    }

    setPhotoUrl(photoUrl: ?string): this {
        return this.clone(this._state.set('photoUrl', photoUrl));
    }

    get language(): string {
        return this._state.get('language');
    }

    setLanguage(language: string): this {
        return this.clone(this._state.set('language', language));
    }

    get timezone(): string {
        return this._state.get('timezone');
    }

    setTimezone(timezone: string): this {
        return this.clone(this._state.set('timezone', timezone));
    }

    get businessUnitIds(): Immutable.List<BusinessUnitId> {
        return this._state.get('businessUnitIds');
    }

    setBusinessUnitIds(businessUnitIds: Immutable.List<BusinessUnitId>): this {
        return this.clone(this._state.set('businessUnitIds', businessUnitIds));
    }

    get personnelType(): PersonnelTypeEnumType {
        return this._state.get('personnelType');
    }

    setPersonnelType(personnelType: PersonnelTypeEnumType): this {
        return this.clone(this._state.set('personnelType', personnelType));
    }

    get transporters(): Immutable.List<TransporterId> {
        return this._state.get('transporters');
    }

    setTransporters(transporters: Immutable.List<TransporterId>): this {
        return this.clone(this._state.set('transporters', transporters));
    }
}
